import { effectSummary } from '../story/effects';
import { enumerateAssignments, tableDistribution, tableOutcomes } from '../story/chainTree';
import { OUTCOME_TONES, type BeatChainDef, type Tone } from '../story/types';

/*
 * The chance events path matrix (chance.html §4 Admin): every option × every hidden-roll combination → outcome, tone, effects and
 * probability, generated from the content. It's how a writer checks that every option can go right or wrong, and that each one can
 * pay off. Rows come from the chain's hidden vars (their default case, like the beat editor's preview) and each option's outcome table;
 * tells don't change outcomes, so they aren't enumerated. "Simulate rolls" in the beat editor runs the real roller on the server.
 */

export type MatrixOutcome = {
  outcomeId: string;
  tone: Tone | '';
  missing: boolean;
  /** P(outcome | this option, this roll). */
  p: number;
  effects: string[];
  /** The beat it leads into (a deeper layer), or "". */
  next: string;
};

export type MatrixRoll = {
  /** Hidden var → value for this combination. */
  vars: Record<string, string>;
  /** P(this combination). */
  p: number;
  outcomes: MatrixOutcome[];
};

export type MatrixOption = {
  beatId: string;
  optionId: string;
  label: string;
  /** The option's `requiresLabel` (it only shows when its condition holds), or "". */
  requires: string;
  toneNote: string;
  rolls: MatrixRoll[];
  /** Some roll (here or in a deeper beat) gives good or windfall. */
  canPayOff: boolean;
  /** Some roll gives bad or disaster. */
  canGoWrong: boolean;
  /** Tone → overall probability of this option's direct outcomes. */
  mix: Record<Tone, number>;
};

export type PathMatrix = {
  /** Hidden var ids (the matrix's roll columns), in chain order. */
  vars: string[];
  options: MatrixOption[];
  /** Beats in the order shown: the root first, then by first reach. */
  beats: string[];
};

const GOOD: readonly string[] = ['good', 'windfall'];
const BAD: readonly string[] = ['bad', 'disaster'];
const isTone = (t: string | undefined): t is Tone =>
  (OUTCOME_TONES as readonly string[]).includes(t ?? '');

/** Beats from the root, breadth first through outcomes' `next`, then any unreached ones. */
function beatOrder(chain: BeatChainDef): string[] {
  const beats = chain.beats ?? {};
  const order: string[] = [];
  const queue = chain.root ? [chain.root] : [];
  while (queue.length) {
    const id = queue.shift()!;
    if (order.includes(id) || !beats[id]) continue;
    order.push(id);
    const b = beats[id]!;
    const outcomes = b.choice
      ? (b.choice.options ?? []).flatMap((o) => tableOutcomes(chain.tables?.[o.table ?? '']))
      : b.outcome
        ? [b.outcome]
        : [];
    for (const o of outcomes) {
      const next = chain.outcomes?.[o]?.next;
      if (next) queue.push(next);
    }
  }
  for (const id of Object.keys(beats)) if (!order.includes(id)) order.push(id);
  return order;
}

/** Every outcome id reachable under a beat (all rolls), following `next`. */
function outcomesUnder(chain: BeatChainDef, beatId: string, seen = new Set<string>()): string[] {
  if (seen.has(beatId)) return [];
  seen.add(beatId);
  const b = chain.beats?.[beatId];
  if (!b) return [];
  const direct = b.choice
    ? (b.choice.options ?? []).flatMap((o) => tableOutcomes(chain.tables?.[o.table ?? '']))
    : b.outcome
      ? [b.outcome]
      : [];
  return direct.flatMap((o) => {
    const next = chain.outcomes?.[o]?.next;
    return next ? [o, ...outcomesUnder(chain, next, seen)] : [o];
  });
}

export function buildPathMatrix(
  chain: BeatChainDef,
  context: Record<string, string> = {},
): PathMatrix {
  const vars = (chain.hidden ?? []).map((v) => v.id ?? '');
  const rolls = enumerateAssignments({ ...chain, tells: [] }, { context });
  const beats = beatOrder(chain);
  const options: MatrixOption[] = [];
  const toneOf = (id: string) => chain.outcomes?.[id]?.tone;

  for (const beatId of beats) {
    const beat = chain.beats?.[beatId];
    if (!beat) continue;
    const opts = beat.choice
      ? (beat.choice.options ?? []).map((o) => ({
          id: o.id ?? '',
          label: o.label ?? '',
          requires: o.requiresLabel ?? '',
          toneNote: o.toneNote ?? '',
          dist: (v: Record<string, string>) =>
            tableDistribution(chain.tables?.[o.table ?? ''], v, context),
        }))
      : [
          {
            id: '',
            label: '(cutscene)',
            requires: '',
            toneNote: '',
            dist: () => new Map(beat.outcome ? [[beat.outcome, 1]] : []),
          },
        ];

    for (const o of opts) {
      const mix: Record<Tone, number> = { disaster: 0, bad: 0, mixed: 0, good: 0, windfall: 0 };
      const all = new Set<string>();
      const rows: MatrixRoll[] = rolls.map((a) => {
        const d = o.dist(a.vars);
        const outcomes: MatrixOutcome[] = [...d].map(([id, p]) => {
          const def = chain.outcomes?.[id];
          all.add(id);
          const tone = isTone(def?.tone) ? (def!.tone as Tone) : '';
          if (tone) mix[tone] += a.p * p;
          return {
            outcomeId: id,
            tone,
            missing: !def,
            p,
            effects: (def?.effects ?? []).map(effectSummary),
            next: def?.next ?? '',
          };
        });
        return { vars: a.vars, p: a.p, outcomes };
      });
      // Deeper layers count too: an outcome that leads into a beat where something pays off.
      const reachable = [...all].flatMap((id) => {
        const next = chain.outcomes?.[id]?.next;
        return next ? [id, ...outcomesUnder(chain, next)] : [id];
      });
      const tones = reachable.map(toneOf);
      options.push({
        beatId,
        optionId: o.id,
        label: o.label,
        requires: o.requires,
        toneNote: o.toneNote,
        rolls: rows,
        canPayOff: tones.some((t) => GOOD.includes(t ?? '')),
        canGoWrong: tones.some((t) => BAD.includes(t ?? '')),
        mix,
      });
    }
  }
  return { vars, options, beats };
}

/** Options that can neither pay off nor say why (the engine's "every option needs a good path or a toneNote" rule, as a hint). */
export const optionsWithoutPayoff = (m: PathMatrix) =>
  m.options.filter((o) => o.optionId !== '' && !o.canPayOff && !o.toneNote.trim());
